import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  /** URL to copy. Default: current page URL. */
  url?: string;
}

/**
 * Copy-to-clipboard share button. Used by every configurator page —
 * the URL itself IS the shareable artifact (the quote id is in the
 * path), so no backend share-link API is required. The configuration
 * the recipient sees is the one stored on the part (KAN-1652), never a
 * copy in the URL.
 */
export function ShareQuoteButton({ url }: Props): JSX.Element {
  const { t } = useTranslation("quote");
  // KAN-1652 — neúspěšné kopírování se dřív tiše spolklo a zákazník
  // nepoznal, jestli má odkaz ve schránce.
  const [result, setResult] = useState<"copied" | "failed" | null>(null);

  return (
    <span className="inline-flex flex-col items-start">
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url ?? window.location.href);
            setResult("copied");
          } catch {
            setResult("failed");
          }
          setTimeout(() => setResult(null), 4000);
        }}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-border-strong bg-surface px-3 text-sm text-ink hover:bg-surface-2"
        data-testid="share-quote"
      >
        <ShareIcon />
        <span>
          {result === "copied"
            ? t("actions.shareCopied")
            : t("actions.shareQuote")}
        </span>
      </button>
      <span role="status" aria-live="polite" className="text-xs text-ink-3">
        {result === "failed" && (
          <span data-testid="share-quote-failed">
            {t("actions.shareCopyFailed")}
          </span>
        )}
      </span>
    </span>
  );
}

function ShareIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <circle cx="12" cy="3.5" r="1.6" />
      <circle cx="4" cy="8" r="1.6" />
      <circle cx="12" cy="12.5" r="1.6" />
      <path d="M5.4 7 L10.6 4.3 M5.4 9 L10.6 11.7" />
    </svg>
  );
}
